import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotification, useUserData } from '../../../../../../../hooks'
import { ConfirmationModal } from '../../../../../../Confirmation'
import {
  postMockApplicantCreditCheckSuggestionAccept,
  postMockApplicantCreditCheckSuggestionDismiss,
} from '../../../../../../RentalPortalApplicant'
import { useApplicantProfileContext } from '../../../../RentalPortalApplicantProfile.hooks'
import { getApplicantProfileTitle } from '../../../../RentalPortalApplicantProfile.utils'
import {
  APPLICANT_CREDIT_CHECK_ACTION,
  QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_DISMISS_CONFIRM,
} from '../../ApplicantCreditCheckBlock.constants'
import { useApplicantCreditCheckVerdict } from '../useApplicantCreditCheckVerdict'

export function useApplicantCreditCheckSuggestion() {
  const { t } = useTranslation()

  const currentUser = useUserData()

  const { handleAddErrorNotification } = useNotification()

  const {
    applicantData,
    applicantId,
    handleCloseModal,
    handleEntityReload,
    setModal,
  } = useApplicantProfileContext()

  const [submittingId, setSubmittingId] = useState(null)

  const { handleOpenVerdictForm } = useApplicantCreditCheckVerdict()

  function handleSuggestionError(onRetry) {
    handleAddErrorNotification({
      textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
      title: t('RentalPortal.ApplicantCreditCheckSuggestionErrorKey'),
      onClickTextButton: onRetry,
    })
  }

  async function handleClickReview(suggestion) {
    setSubmittingId(suggestion.id)

    try {
      const { data } = await postMockApplicantCreditCheckSuggestionAccept({
        applicantId,
        suggestionId: suggestion.id,
      })

      await handleEntityReload()

      handleOpenVerdictForm(
        APPLICANT_CREDIT_CHECK_ACTION.recordVerdict,
        data.extraction
      )
    } catch {
      handleSuggestionError(() => handleClickReview(suggestion))
    }

    setSubmittingId(null)
  }

  async function dismissSuggestion(suggestion) {
    try {
      await postMockApplicantCreditCheckSuggestionDismiss({
        applicantId,
        currentUser,
        suggestionId: suggestion.id,
      })

      handleCloseModal()

      await handleEntityReload()
    } catch {
      handleSuggestionError(() => dismissSuggestion(suggestion))
    }
  }

  function handleClickDismiss(suggestion) {
    async function handleSubmitDismiss() {
      return dismissSuggestion(suggestion)
    }

    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t(
            'RentalPortal.ApplicantCreditCheckSuggestionDismissBtnKey'
          ),
        }}
        description={t(
          'RentalPortal.ApplicantCreditCheckSuggestionDismissTextKey',
          {
            applicant: getApplicantProfileTitle({ applicantData, t }),
            file: suggestion.file.name,
          }
        )}
        qaName={QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_DISMISS_CONFIRM}
        title={t('RentalPortal.ApplicantCreditCheckSuggestionDismissTitleKey')}
        onCancel={handleCloseModal}
        onSubmit={handleSubmitDismiss}
      />
    )
  }

  return { handleClickDismiss, handleClickReview, submittingId }
}
